/**
 * Assets on a phone and an iPad, in the Season design: the library as groups, a folder as a grid, a file as a sheet
 * (the detail pane on the iPad's side) and Trash as the same grid. It reads what the computer's Assets reads
 * (`useAssetsData`, `useFileActions`) and draws the same library.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { LibraryAsset } from "../../shared/types";
import { DockedBar } from "../components/season/DockedBar";
import { Empty } from "../components/season/Empty";
import type { SeasonLayout } from "../components/season/layout";
import { Page } from "../components/season/Page";
import { PageHead } from "../components/season/PageHead";
import { Pane, Panes } from "../components/season/Panes";
import { PushedNav } from "../components/season/PushedNav";
import { Search } from "../components/season/Search";
import { Switch } from "../components/season/Switch";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Field } from "../components/ui/Field";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import "../styles/season-assets.css";
import { FolderMenu } from "./FolderActions";
import { ALL, FITS, type KindGroup, fileLine, filesUnder, folderName, groupKids, kindChoices, kindGroupOf, listFiles, searchPath, trashLine, uploadTarget } from "./library";
import { upLine } from "./parts";
import { FilePane, FileSheet, type FileProps } from "./SeasonFile";
import { Grid, Groups, TRASH, Tile } from "./seasonParts";
import { createFolder, loadLibrary, uploadFiles, uploadsAsAssets } from "./store";
import { useAssetsData, useFileActions, useFollowFolder } from "./useAssets";
import { useRailFold } from "./useRailFold";

const MAX_NAME = 40;

function NewFolder({ where, onClose, onMake }: { where: string; onClose: () => void; onMake: (name: string) => void }) {
  const [name, setName] = useState("");
  const [nudge, setNudge] = useState(false);
  const make = () => {
    if (!name.trim()) {
      setNudge(true);
      return;
    }
    onMake(name.trim());
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title="New folder"
      footer={
        <>
          <Button variant="p" onClick={make}>
            Create
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <Field label="Folder name" htmlFor="sa-new" hint={`In ${where}`} error={nudge ? "Give the folder a name first." : undefined}>
        <input dir="auto"
          id="sa-new"
          className="inp"
          autoComplete="off"
          maxLength={MAX_NAME}
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNudge(false);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            make();
          }}
        />
      </Field>
    </Sheet>
  );
}

const filesWord = (n: number) => `${n} file${n === 1 ? "" : "s"}`;

export function SeasonAssets({ layout }: { layout: SeasonLayout }) {
  const { lib, flashkut, blind, canChange, folders, shown, trashed } = useAssetsData();
  const actions = useFileActions(folders);
  const toast = useToast();
  const navigate = useNavigate();
  const { folder: routeFolder } = useParams();
  const [params] = useSearchParams();
  const phone = layout === "phone";
  const side = layout === "side";
  const inTrash = params.get("view") === "trash";
  const folder = routeFolder && (routeFolder === ALL || folders.some((f) => f.id === routeFolder)) ? routeFolder : ALL;
  const place = inTrash ? TRASH : folder;
  // Why the phone's own start: with no folder named it shows the groups; the computer's "/assets" is All files.
  const root = phone && !routeFolder && !inTrash;
  const [q, setQ] = useState("");
  const [searchKey, setSearchKey] = useState(0);
  const [kind, setKind] = useState<KindGroup>("all");
  const [sel, setSel] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void loadLibrary();
  }, []);
  useEffect(() => {
    setKind("all");
    setSel(null);
  }, [place]);

  const searching = !!q.trim();
  const target = uploadTarget(folders, folder);
  const pending = lib.uploads.filter((u) => u.from === "page");
  const ups = new Map(pending.map((u) => [u.id, u]));
  const all = [...uploadsAsAssets(pending), ...shown];
  const first = new Set([...pending.map((u) => u.id), ...lib.fresh]);
  const found = searching ? listFiles(folders, all, { mode: "page", folder: ALL, q, fit: FITS.any }, first) : filesUnder(folders, all, folder, first);
  const choices = searching || inTrash ? [] : kindChoices(found);
  const kindNow = choices.some((c) => c.value === kind) ? kind : "all";
  const list = inTrash && !searching ? trashed : kindNow === "all" ? found : found.filter((a) => kindGroupOf(a) === kindNow);
  const total = inTrash && !searching ? trashed.length : found.length;
  const openable = list.filter((a) => !ups.has(a.id));
  const picked = (inTrash && !searching ? trashed : shown).find((x) => x.id === sel) ?? null;
  const current = side ? (openable.find((x) => x.id === sel) ?? openable[0] ?? null) : picked;
  const uploadable = canChange && !inTrash && (folder === ALL || target === folder);
  const own = folders.find((f) => f.id === folder);

  const fold = useRailFold(folders, all, q, place);
  const follow = useFollowFolder(folder, "/assets/all");
  const open = (id: string) => {
    setQ("");
    setSearchKey((k) => k + 1);
    navigate(id === ALL ? (phone ? "/assets/all" : "/assets") : `/assets/${encodeURIComponent(id)}`);
  };
  const openTrash = () => {
    setQ("");
    setSearchKey((k) => k + 1);
    navigate("/assets?view=trash");
  };
  const upload = (files: File[]) => {
    if (!files.length || blind) return;
    if (searching) {
      setQ("");
      setSearchKey((k) => k + 1);
    }
    const again = uploadFiles(files, target, "page", (done) => {
      if (side) setSel(done.id);
      toast(`Ready in ${folderName(folders, done.folder_id)}.`);
    });
    for (const name of again) toast(`${name} is already going up.`);
  };

  const fileProps = (a: LibraryAsset): FileProps => {
    const trash = trashed.some((x) => x.id === a.id);
    return {
      a,
      folders,
      trash,
      own: !trash && a.source === "upload" && canChange,
      talk: !trash && FITS.talk.test(a) && (a.source !== "flashkut" || flashkut),
      making: actions.making === a.id,
      onMakeRoom: () => void actions.makeRoom(a),
      onMove: (to) => {
        setSel(null);
        void actions.move(a, to);
      },
      onDelete: () => {
        setSel(null);
        void actions.remove(a);
      },
      onRestore: () => {
        setSel(null);
        void actions.restore([a.id]);
      },
      onGo: (href) => navigate(href),
    };
  };

  const placeName = inTrash ? "Trash" : folder === ALL ? "All files" : folderName(folders, folder);
  const tile = (a: LibraryAsset) => {
    const u = ups.get(a.id);
    if (u) return <Tile key={a.id} a={a} sub="" up={{ line: upLine(u, folderName(folders, u.folderId)), pct: u.stage === 0 ? u.pct : 100, fail: !!u.error }} />;
    const sub = searching ? searchPath(folders, a) : inTrash ? trashLine(a, folders) : fileLine(a);
    return <Tile key={a.id} a={a} sub={sub} on={side && current?.id === a.id} onClick={() => setSel(a.id)} />;
  };

  const groups = (
    <Groups
      folders={folders}
      assets={all}
      open={root ? null : place}
      fold={fold}
      blind={blind}
      onOpen={open}
      trash={{ count: trashed.length, onOpen: openTrash }}
      onNewFolder={canChange && !blind ? () => setNewFolder(true) : undefined}
    />
  );
  const search = <Search key={searchKey} placeholder={phone ? "Search every file and folder" : "Search"} label="Search every file and folder" onSearch={setQ} />;

  const body = (() => {
    if (lib.status === "loading" && !shown.length) {
      return (
        <p className="k-sub" aria-busy="true">
          Loading
        </p>
      );
    }
    if (lib.status === "error") {
      return (
        <Empty title={lib.error ?? "We couldn’t open Assets."}>
          <Button onClick={() => void loadLibrary(true)}>Try again</Button>
        </Empty>
      );
    }
    if (!list.length) {
      const said = searching ? `Nothing matches “${q.trim()}”` : inTrash ? "Trash is empty" : `${placeName} is empty`;
      return <Empty title={said} />;
    }
    const guests = !searching && !inTrash && folder === "room/guests" ? groupKids(folders, folder).filter((k) => list.some((a) => a.folder_id === k.id)) : [];
    if (guests.length) {
      return (
        <>
          {guests.map((k) => {
            const mine = list.filter((a) => a.folder_id === k.id);
            return (
              <section key={k.id}>
                <h3 className="sa-gg">
                  <Face name={k.name} size={32} />
                  <b>{k.name}</b>
                  <span>{mine.length}</span>
                </h3>
                <Grid>{mine.map(tile)}</Grid>
              </section>
            );
          })}
        </>
      );
    }
    return <Grid>{list.map(tile)}</Grid>;
  })();

  const kinds = choices.length ? <Switch options={choices} value={kindNow} onChange={setKind} label="Kind" /> : null;
  const uploadButton = (full = false) => (
    <Button variant="p" full={full} icon="upload" disabled={blind} onClick={() => input.current?.click()}>
      Upload
    </Button>
  );
  const menu =
    canChange && !inTrash && !searching && own && !own.system ? (
      <FolderMenu
        key={own.id}
        folder={own}
        folders={folders}
        assets={shown}
        buttonClassName="k-ib"
        onChanged={(to) => {
          setSel(null);
          follow(own.id, to);
        }}
      />
    ) : null;
  const mainHead = (
    <div className="sa-mh">
      <h2>{searching ? "Search" : placeName}</h2>
      {searching ? <span className="sa-mc">{filesWord(total)}</span> : null}
      <span className="sa-mr">
        {menu}
        {side && uploadable ? uploadButton() : null}
      </span>
    </div>
  );
  const sheets = (
    <>
      {!side && picked ? <FileSheet key={picked.id} p={fileProps(picked)} onClose={() => setSel(null)} /> : null}
      {newFolder ? (
        <NewFolder
          where={folderName(folders, target)}
          onClose={() => setNewFolder(false)}
          onMake={(name) => {
            void createFolder(name, target).then(
              (f) => {
                setNewFolder(false);
                open(f.id);
              },
              (e: unknown) => toast({ text: e instanceof Error ? e.message : "That folder wasn’t made.", error: true }),
            );
          }}
        />
      ) : null}
      <input
        ref={input}
        type="file"
        hidden
        multiple
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          upload(files);
        }}
      />
    </>
  );

  if (phone) {
    const bar = uploadable ? <DockedBar>{uploadButton(true)}</DockedBar> : null;
    if (root) {
      return (
        <Page layout={layout} className="sa">
          <PushedNav back={{ to: "/more", label: "More" }} />
          <PageHead title="Assets" />
          {search}
          {searching ? (
            <>
              <h2 className="sa-mh">
                <b>Search</b>
                <span>{filesWord(total)}</span>
              </h2>
              {body}
            </>
          ) : (
            groups
          )}
          {bar}
          {sheets}
        </Page>
      );
    }
    return (
      <Page layout={layout} className="sa">
        <PushedNav back={{ to: "/assets", label: "Assets" }} right={menu} />
        <h1 className="k-h1">{placeName}</h1>
        <p className="k-meta sa-cnt">{filesWord(total)}</p>
        {kinds}
        {body}
        {bar}
        {sheets}
      </Page>
    );
  }

  if (!side) {
    return (
      <Page layout={layout} narrow className="sa">
        <PageHead title="Assets" right={uploadable ? uploadButton() : undefined} />
        <div className="sa-two">
          <aside className="sa-rail" aria-label="Folders">
            {search}
            {groups}
          </aside>
          <section className="sa-main" aria-label="Files">
            {mainHead}
            {kinds}
            {body}
          </section>
        </div>
        {sheets}
      </Page>
    );
  }

  return (
    <>
      <Panes three className="sa-split">
        <Pane kind="list" keep="assets-list" label="Folders">
          <div className="k-sp-lh">
            <div className="k-head sa-lh">
              <h1 className="k-title">Assets</h1>
            </div>
          </div>
          <Page layout="side" className="sa sa-rail">
            {search}
            {groups}
          </Page>
        </Pane>
        <Pane kind="main" keep="assets-main" showing={place} label="Files">
          <Page layout="side" className="sa">
            {mainHead}
            {kinds}
            {body}
          </Page>
        </Pane>
        <Pane kind="detail" showing={current?.id ?? null} label="The file that is open">
          <Page layout="side" className="sa">
            {current ? <FilePane key={current.id} p={fileProps(current)} /> : <Empty title="Pick a file" />}
          </Page>
        </Pane>
      </Panes>
      {sheets}
    </>
  );
}
